import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Loader2, Check, X, RefreshCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { relativeTime, initials } from "@/lib/utils-app";

/** Meddelanden + textfält. Handläggare kan formalisera sin kommentar med AI. */
export default function CaseComments({ comments = [], userId, isAdmin, caseId, text, setText, onSend }) {
  const endRef = useRef(null);
  const [formalizing, setFormalizing] = useState(false);
  const [proposal, setProposal] = useState(null); // { formalized, changes_summary, original }

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const runFormalize = async () => {
    if ((text || "").trim().length < 10) {
      toast.error("Skriv minst 10 tecken innan du formaliserar");
      return;
    }
    setFormalizing(true);
    try {
      const { data } = await api.post(`/crm/comments/formalize`, { case_id: caseId, text });
      setProposal(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Formalisering misslyckades");
    } finally {
      setFormalizing(false);
    }
  };

  const acceptProposal = () => {
    if (proposal?.formalized) {
      setText(proposal.formalized);
      api.post(`/crm/comments/formalize/accept`, { case_id: caseId }).catch(() => {});
      toast.success("Förslag ersätter din text – klicka på skicka när du är nöjd");
    }
    setProposal(null);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6">
      <h3 className="text-lg font-medium mb-4" style={{ fontFamily: "Outfit" }}>Meddelanden</h3>
      <div className="space-y-4 max-h-96 overflow-y-auto pr-2" data-testid="comments-list">
        {comments.length === 0 && <p className="text-sm text-zinc-500 text-center py-8">Inga meddelanden ännu.</p>}
        {comments.map((m) => {
          const mine = m.created_by === userId;
          return (
            <div key={m.comment_id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
              <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-700 flex-shrink-0">
                {initials(m.created_by_name)}
              </div>
              <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`text-xs mb-1 ${mine ? "text-right" : ""} text-zinc-500`}>
                  <span className="font-medium text-zinc-700">{m.created_by_name}</span>
                  {m.created_by_role === "admin" && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm">
                      Handläggare
                    </span>
                  )}
                  <span className="ml-2">{relativeTime(m.created_at)}</span>
                </div>
                <div className={`px-3 py-2 rounded-sm text-sm whitespace-pre-line ${
                  mine ? "bg-blue-700 text-white" : "bg-zinc-50 border border-zinc-200 text-zinc-900"
                }`}>
                  {m.text}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {proposal && (
        <div className="mt-4 border border-purple-200 bg-purple-50 rounded-sm p-4" data-testid="comment-formalize-proposal">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-purple-700">
              <Sparkles className="w-3.5 h-3.5" /> AI-förfinat förslag
            </div>
            <button onClick={() => setProposal(null)} data-testid="proposal-close" className="p-1 hover:bg-purple-100 rounded-sm">
              <X className="w-3.5 h-3.5 text-zinc-500" />
            </button>
          </div>
          <div className="text-sm text-zinc-900 whitespace-pre-wrap bg-white border border-purple-200 rounded-sm p-3 mb-2" data-testid="proposal-text">
            {proposal.formalized}
          </div>
          {proposal.changes_summary && (
            <p className="text-xs text-zinc-600 mb-3 italic">Ändringar: {proposal.changes_summary}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={runFormalize} disabled={formalizing} data-testid="proposal-retry"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-zinc-300 hover:bg-zinc-100 rounded-sm">
              <RefreshCcw className="w-3 h-3" /> Generera igen
            </button>
            <button onClick={acceptProposal} data-testid="proposal-accept"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-700 hover:bg-purple-800 text-white rounded-sm">
              <Check className="w-3 h-3" /> Använd förslag
            </button>
          </div>
        </div>
      )}

      <form onSubmit={onSend} className="mt-4 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Skriv ett meddelande..."
          data-testid="comment-input"
          className="flex-1 px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
        />
        {isAdmin && (
          <button type="button" onClick={runFormalize} disabled={formalizing || (text || "").trim().length < 10}
            data-testid="comment-formalize-btn"
            title="Låt AI förfina din kommentar innan du skickar"
            className="px-3 bg-purple-700 hover:bg-purple-800 text-white rounded-sm flex items-center gap-1.5 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed text-sm">
            {formalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          </button>
        )}
        <button type="submit" data-testid="comment-submit"
          className="px-4 bg-blue-700 hover:bg-blue-800 text-white rounded-sm flex items-center gap-2 active:scale-[0.98]">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
